<svelte:options
  customElement={{
    tag: "oc-floating-focus-v1",
    extend: window.__components.extend({ asyncInit: true }),
  }}
/>

<script lang="ts" module>
  import { onLoad } from "@otto-ec/global-resources/dom";
  import { unsetAll } from "@otto-ec/global-resources/event";
  import { onMount, untrack } from "svelte";
  import { on } from "svelte/events";

  import { track } from "../../../common/utils/track";
  import { findNextPositionedParent } from "./utils/findNextPositionedParent";
  import { repositionElement } from "./utils/floaterUtils";
  import { useActiveElement } from "./utils/useActiveElement.svelte";
  import { canUseHtmlPopoverApi } from "../../../common/utils/canUseHtmlPopoverApi.ts";

  const focusTagname = "oc-floating-focus-v1";

  if (canUseHtmlPopoverApi)
    onLoad.subscribe(() => {
      if (!document.querySelector(focusTagname)) {
        document.body.append(document.createElement(focusTagname));
      }
    });
</script>

<script lang="ts">
  import { useInterval } from "@otto-ec/otto-components-utils/svelte/useInterval";
  import { useDialogPopoverInterceptor } from "./utils/useDialogPopoverInterceptor.svelte.ts";
  import { useMask } from "./utils/useMask.svelte.ts";
  import { pointerType } from "@otto-ec/global-resources/device";

  let Host = $host();

  let isKeyboardUser: boolean = $state(false);

  let floater = $state<HTMLElement>();

  /*                                                                          */
  let focusTarget = $derived.by(useActiveElement());

  /*                                         */
  let floaterTarget = $derived.by(() => {
    if (!isKeyboardUser) return undefined;
    if (!focusTarget) return undefined;

    /*                                                             */
    if (document.body === focusTarget) return undefined;

    return getComputedStyle(focusTarget).getPropertyValue("--oc-floating-focus-v1-full-bleed")
      ? findNextPositionedParent(focusTarget)
      : focusTarget;
  });

  let active = $derived(!$pointerType.isTouchable && isKeyboardUser && floaterTarget);

  /*                                                        */
  let floaterTargetIsMoving: boolean = $state(false);
  let floaterTargetRect: DOMRect | undefined = $state(undefined);

  $effect(() => {
    track(floaterTarget);
    floaterTargetIsMoving = false;
    floaterTargetRect = undefined;
  });

  const handleFloaterState = (): void => {
    if (!floaterTarget) return;
    repositionElement(floater!, floaterTarget);

    /*                                    */
    const newRect = floaterTarget.getBoundingClientRect();
    floaterTargetIsMoving =
      !!floaterTargetRect &&
      !(
        newRect.x === floaterTargetRect.x &&
        newRect.y === floaterTargetRect.y &&
        newRect.width === floaterTargetRect.width &&
        newRect.height === floaterTargetRect.height
      );
    floaterTargetRect = newRect;

    if (
      document.body === Host.parentElement &&
      getComputedStyle(document.body).position === "static"
    ) {
      /*                                                                      */
      Host.style.left = `0`;
      Host.style.top = `0`;
    } else {
      /*                                                                              */
      /*                                                                            */
      Host.style.left = `${-(Host.parentElement?.getBoundingClientRect().x ?? 0)}px`;
      Host.style.top = `${-(Host.parentElement?.getBoundingClientRect().y ?? 0)}px`;
    }
  };

  $effect(() => {
    track(floaterTarget);
    untrack(() => {
      handleFloaterState();
    });
  });

  $effect(() => {
    if (active) useInterval(handleFloaterState, 100);
  });

  let outlineOffset = $derived.by(() => {
    if (!floaterTarget) return undefined;
    return (
      getComputedStyle(floaterTarget).getPropertyValue("--oc-floating-focus-v1-offset") || "0px"
    );
  });

  let borderRadius = $derived.by(() => {
    if (!floaterTarget) return undefined;

    const customBorderRadius = getComputedStyle(floaterTarget).getPropertyValue(
      "--oc-floating-focus-v1-radius",
    );

    if (customBorderRadius) return customBorderRadius;

    let minRadius = getComputedStyle(Host).borderRadius;
    let targetBorderRadius = getComputedStyle(floaterTarget).borderRadius;

    return parseInt(targetBorderRadius, 10) < parseInt(minRadius, 10)
      ? undefined
      : `calc(${parseInt(targetBorderRadius, 10)}px + ${outlineOffset})`;
  });

  let spacing = $derived.by(() => {
    if (!focusTarget) return undefined;
    if (!floaterTarget) return undefined;
    return (
      getComputedStyle(focusTarget).getPropertyValue("--oc-floating-focus-v1-spacing") || "0px"
    );
  });

  const handleMouseDown = (): void => {
    isKeyboardUser = false;
  };

  const handleKeyDown = (e: KeyboardEvent): void => {
    if (
      ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key) &&
      focusTarget?.classList.contains("card__interactive")
    )
      return;

    if (!["Tab", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) return;
    isKeyboardUser = true;
  };

  onMount(() => on(window, "keydown", handleKeyDown, { passive: true, capture: true }));

  let isScrolling = $state(false);
  let isScrollingInterval: NodeJS.Timeout;
  const handleScrollResize = (): void => {
    clearInterval(isScrollingInterval);
    isScrolling = true;
    handleFloaterState();
    isScrollingInterval = setTimeout(() => (isScrolling = false), 100);
  };

  /*                                                        */
  let noTransition = $state(false);
  $effect(() => {
    if (floaterTarget) {
      requestAnimationFrame(() => {
        noTransition = false;
      });
    } else {
      noTransition = true;
    }
  });

  /*                                             */
  $effect(() => {
    if (!floaterTarget) return;
    if (getComputedStyle(floaterTarget).getPropertyValue("--oc-floating-focus-v1-preserve-outline"))
      return;

    floaterTarget.classList.add("oc-floating-focus-v1-hide-outline");
    return () => floaterTarget.classList.remove("oc-floating-focus-v1-hide-outline");
  });

  $effect(() => {
    /*                                                                                     */
    if (!floaterTarget)
      Object.assign(floater!.style, {
        top: "0",
        left: "0",
        width: "0",
        height: "0",
      });
  });

  /*                                      */
  $effect(() => {
    /*                                                               */
    /*                                                  */
    if ($pointerType.isTouchable) return;

    if (isKeyboardUser && floaterTarget) {
      floater?.showPopover();
    } else {
      floater?.hidePopover();
    }
  });

  /*                                                                     */
  const handleDialogPopoverToggle = (): void => {
    if (!active) return;
    floater?.hidePopover();
    floater?.showPopover();
  };

  $effect(() => {
    if (isKeyboardUser) {
      return unsetAll(
        on(document.body, "scroll", handleScrollResize, { passive: true, capture: true }),
        on(window, "resize", handleScrollResize, { passive: true, capture: true }),
        on(window, "mousedown", handleMouseDown, { passive: true, capture: true }),
        on(window, "touchstart", handleMouseDown, { passive: true, capture: true }),
      );
    }
  });

  useDialogPopoverInterceptor(() => floater, handleDialogPopoverToggle);

  useMask(
    () => floater,
    () => floaterTarget,
  );
</script>

<div
  popover="manual"
  bind:this={floater}
  class="floating-focus"
  class:floating-focus--visible={floaterTarget}
  class:floating-focus--inactive={$pointerType.isTouchable && !isKeyboardUser}
  class:floating-focus--stopped={isScrolling || noTransition || floaterTargetIsMoving}
  style:--radius={borderRadius}
  style:--offset={outlineOffset}
  style:--spacing={spacing}
></div>

<style lang="scss">
  @use "@otto-ec/design-tokens/component" as tokens;

  :global {
    :host {
      position: absolute;
      left: 0;
      top: 0;
      border-radius: #{tokens.$oc-semantic-focus-outline-radius-12}; /*                                           */
    }

    .floating-focus {
      --outline-offset: calc(var(--offset) + #{tokens.$oc-semantic-focus-outline-offset});
      --overshoot: calc(#{tokens.$oc-semantic-focus-outline-radius-12} + var(--outline-offset));

      margin: 0;
      inset: unset;
      position: absolute;
      transform: translate(-50%, -50%);
      opacity: 0;
      will-change: top, left, width, height, border-width, border-radius;
      box-sizing: content-box;
      pointer-events: none;
      overflow: hidden;
      outline: tokens.$oc-semantic-focus-outline-radius-12 solid
        tokens.$oc-semantic-focus-outline-color;
      background: transparent;
      padding: var(--spacing, 0);

      border-radius: calc(var(--radius, 0px) + var(--overshoot));

      /*                                                                                                    */
      border: var(--overshoot) solid transparent;
      /*                                                                                 */
      outline-offset: calc(-1 * #{tokens.$oc-semantic-focus-outline-radius-12});

      mask-image:
        linear-gradient(#000, #000), linear-gradient(#000, #000), linear-gradient(#000, #000),
        linear-gradient(#000, #000);

      mask-repeat: no-repeat;
      mask-origin: border-box;
      mask-clip: border-box;

      mask-position:
        0 0,
        0 calc(var(--mask-top) + var(--mask-height) + var(--overshoot)),
        0 calc(var(--mask-top) + var(--overshoot)),
        calc(var(--mask-left) + var(--mask-width) + var(--overshoot))
          calc(var(--mask-top) + var(--overshoot));

      mask-size:
        100% calc(var(--mask-top) + var(--overshoot)),
        100% 100%,
        calc(var(--mask-left) + var(--overshoot)) var(--mask-height),
        100% var(--mask-height);

      &--visible {
        opacity: 1;
      }

      &--stopped {
        transition: none;
      }

      @media (prefers-reduced-motion: no-preference) {
        transition-property: left, top, width, height, border-width, border-radius;
        transition-duration: 0.1s, 0.1s, 0.1s, 0.1s, 0.1s, 0.1s;
        transition-timing-function: ease, ease, ease, ease, ease, ease;
      }
    }
  }
</style>
